import { apiService } from './api';
import { API_BASE_URL as API_BASE } from '../config/api';
import {
  StockTransferDTO,
  StockTransferListDTO,
  StockTransferApprovalStatusDTO,
  StockTransferStatsDTO,
  CreateStockTransferRequest,
  TransferOutItemRequest,
  TransferInItemRequest,
} from '../types';

const API = '/stock-transfers';

export const stockTransferService = {
  // ── CRUD ──────────────────────────────────────────────────────────────────

  getAll: (pageNumber = 1, pageSize = 50, statuses?: string) =>
    apiService.get<{ data: StockTransferListDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      API, statuses ? { pageNumber, pageSize, statuses } : { pageNumber, pageSize }
    ),

  getById: (id: number | string) =>
    apiService.get<StockTransferDTO>(`${API}/${id}`),

  getStats: () =>
    apiService.get<StockTransferStatsDTO>(`${API}/stats`),

  create: (data: CreateStockTransferRequest) =>
    apiService.post<StockTransferDTO>(API, data),

  update: (id: number | string, data: Partial<CreateStockTransferRequest> & { items: CreateStockTransferRequest['items'] }) =>
    apiService.put<StockTransferDTO>(`${API}/${id}`, data),

  delete: (id: number | string) =>
    apiService.delete<boolean>(`${API}/${id}`),

  // ── Approval ──────────────────────────────────────────────────────────────

  submit: (id: number | string, submittedByUserId: number) =>
    apiService.post<StockTransferApprovalStatusDTO>(`${API}/${id}/submit`, { submittedByUserId }),

  getApprovalStatus: (id: number | string) =>
    apiService.get<StockTransferApprovalStatusDTO>(`${API}/${id}/approvals`),

  approve: (approvalId: number, userId: number, comments?: string) =>
    apiService.post<StockTransferApprovalStatusDTO>(
      `${API}/approvals/${approvalId}/approve`,
      { userId, comments }
    ),

  reject: (approvalId: number, userId: number, rejectionReason: string) =>
    apiService.post<StockTransferApprovalStatusDTO>(
      `${API}/approvals/${approvalId}/reject`,
      { userId, rejectionReason }
    ),

  getPendingApprovals: (userId: number, pageNumber = 1, pageSize = 50) =>
    apiService.get<{ data: StockTransferApprovalStatusDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      `${API}/pending-approvals`,
      { userId, pageNumber, pageSize }
    ),

  // ── Transfer operations ───────────────────────────────────────────────────

  transferOut: (id: number | string, transferredByUserId: number, items: TransferOutItemRequest[]) =>
    apiService.post<StockTransferDTO>(`${API}/${id}/transfer-out`, { transferredByUserId, items }),

  transferIn: (id: number | string, receivedByUserId: number, items: TransferInItemRequest[]) =>
    apiService.post<StockTransferDTO>(`${API}/${id}/transfer-in`, { receivedByUserId, items }),

  cancel: (id: number | string, cancelledByUserId: number, cancellationReason?: string) =>
    apiService.post<StockTransferDTO>(`${API}/${id}/cancel`, { cancelledByUserId, cancellationReason }),

  // ── PDF ────────────────────────────────────────────────────────────────────

  /**
   * Opens the Transfer Out (Dispatch Note) PDF in a new browser tab.
   * Available once the transfer has been dispatched (status: TransferredOut / PartiallyReceived / Completed).
   */
  openTransferOutPdf: async (id: number | string): Promise<void> => {
    const token    = localStorage.getItem('auth_token');
    const response = await fetch(
      `${API_BASE}/stock-transfers/${id}/transfer-out-pdf`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error(`Failed to fetch Dispatch Note PDF (${response.status})`);
    const blob = await response.blob();
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    if (!win) throw new Error('Popup blocked — please allow popups for this site.');
  },

  /**
   * Opens the Transfer In (Receipt Note) PDF in a new browser tab.
   * Available once a receipt has been recorded (status: PartiallyReceived / Completed).
   */
  openTransferInPdf: async (id: number | string): Promise<void> => {
    const token    = localStorage.getItem('auth_token');
    const response = await fetch(
      `${API_BASE}/stock-transfers/${id}/transfer-in-pdf`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error(`Failed to fetch Receipt Note PDF (${response.status})`);
    const blob = await response.blob();
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    if (!win) throw new Error('Popup blocked — please allow popups for this site.');
  },
};
